import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useSearch } from "wouter";
import { Button, DataTable, EmptyState, Loader, Tabs } from "@threadws/jute";
import type { Persona } from "../../api/generated";
import { meMeOptions, personasListOptions, usersListOptions } from "../../api/generated/@tanstack/react-query.gen";
import { ConnectionNotice } from "./connections/ConnectionNotice";
import { formatDate } from "../../utils/formatDate";
import { userName } from "../../utils/userName";
import { DeletePersonaDialog } from "./DeletePersonaDialog";

const columns = [
  { key: "name", label: "Name" },
  { key: "people", label: "People" },
  { key: "updated", label: "Updated" },
  { key: "actions", label: "", align: "right" as const },
];

/** Personas, showing your own by default. `?show=all` lists everyone's. */
export function PersonasListPage() {
  const [, navigate] = useLocation();
  const showAll = new URLSearchParams(useSearch()).get("show") === "all";
  const queries = [useQuery(personasListOptions()), useQuery(usersListOptions()), useQuery(meMeOptions())] as const;
  const [{ data: personas }, { data: users }, { data: me }] = queries;
  const [toDelete, setToDelete] = useState<Persona | null>(null);
  const create = () => navigate("/personas/new");
  const showTab = (tab: string) => navigate(tab === "All" ? "/personas?show=all" : "/personas");

  const error = queries.find((query) => query.error)?.error;
  if (error) return <EmptyState title="Couldn't load personas" description={error.message} />;
  if (!personas || !users || !me) return <Loader label="Loading personas" />;

  const owners = (personaId: string) => users.filter((user) => user.personaIds.includes(personaId));
  const visible = showAll
    ? personas
    : personas.filter((persona) => owners(persona.id).some((user) => user.id === me.id));

  const rows = visible.map((persona) => ({
    id: persona.id,
    name: (
      <Link href={`/personas/${persona.id}`} className="text-text-link hover:text-text-link-hover">
        {persona.name}
      </Link>
    ),
    people: <span className="text-text-secondary">{owners(persona.id).map(userName).join(", ") || "No one"}</span>,
    updated: formatDate(persona.updatedAt),
    actions: (
      <Button variant="ghost" size="sm" icon="" onClick={() => setToDelete(persona)}>
        Delete
      </Button>
    ),
  }));

  return (
    <section className="flex w-full max-w-3xl flex-col gap-6 px-4">
      <ConnectionNotice />
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-medium">Personas</h1>
        {personas.length > 0 && <Button onClick={create}>New persona</Button>}
      </header>
      {personas.length === 0 ? (
        <EmptyState
          title="No personas yet"
          description="A persona gives an agent its description and rules."
          actionLabel="Create a persona"
          onAction={create}
        />
      ) : (
        <>
          <Tabs tabs={["Mine", "All"]} active={showAll ? "All" : "Mine"} onChange={showTab} />
          {rows.length === 0 ? (
            <EmptyState
              title="None of these are yours yet"
              description="Open a persona and tick your name under People to make it yours."
              actionLabel="Show all personas"
              onAction={() => showTab("All")}
            />
          ) : (
            <DataTable columns={columns} rows={rows} />
          )}
        </>
      )}
      <DeletePersonaDialog persona={toDelete} onClose={() => setToDelete(null)} />
    </section>
  );
}
